import clsx from 'clsx'
import {getBranchNameById} from 'common/HelperFunctions'
import Spinner from 'common/components/Spinner'
import {ONBOARD_PROGRESS} from 'common/constants'
import {useMultiStepForm} from 'common/hooks/useMultiStepForm'
import {lazy, useCallback, useEffect, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import Modal from 'react-bootstrap/Modal'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {trackProgressBar} from 'setup/redux/Actions/BulkUpload/bulkUploadCsv'

import {getCSSVariableValue} from '_metronic/assets/ts/_utils'

const BranchModal = lazy(() => import('./components/BranchModal'))
const FinishModal = lazy(() => import('./components/FinishModal'))
const WelcomeModal = lazy(() => import('./components/WelcomeModal'))
const ClassSubjectModal = lazy(() => import('./components/ClassSubjectModal'))
const StudentParentModal = lazy(() => import('./components/StudentParentModal'))

const toolbarButtonMarginClass = 'ms-1 ms-lg-3'

const initialValues = {
  branchId: parseInt(localStorage.getItem('branch') || '1', 10),
  branchName: getBranchNameById(parseInt(localStorage.getItem('branch') || '1', 10)),
  onboardProgress: 0,
  opType: '',
  teacherIdentifier: '',
}

const ClientOnboardPage = () => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const [show, setShow] = useState(false)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(initialValues)
  const [onboardProgress, setOnboardProgress] = useState(0)

  const updateFields = useCallback((fields: any) => {
    setData((prev) => ({...prev, ...fields}))
  }, [])

  const {steps, currentStepIndex, step, back, next, reset, goto} = useMultiStepForm([
    <WelcomeModal key='welcome-modal' onNext={() => next()} />,
    <BranchModal
      key='branch-modal'
      data={data}
      updateFields={updateFields}
      onNext={handleNext}
      onBack={() => back()}
    />,
    <ClassSubjectModal
      key='class-subject-modal'
      data={data}
      updateFields={updateFields}
      onNext={handleNext}
      onBack={() => back()}
    />,
    <StudentParentModal
      key='student-parent-modal'
      data={data}
      onNext={handleNext}
      onBack={() => back()}
    />,
    <FinishModal key='finish-modal' />,
  ])

  const branchStepProgress = currentStepIndex === 1 && onboardProgress < ONBOARD_PROGRESS.STEP_1
  const ClassSubjectStepProgress =
    currentStepIndex === 2 && onboardProgress < ONBOARD_PROGRESS.STEP_2
  const UserStepProgress = currentStepIndex === 3 && onboardProgress < ONBOARD_PROGRESS.LAST_STEP

  async function handleNext() {
    if (
      onboardProgress !== ONBOARD_PROGRESS.LAST_STEP &&
      (branchStepProgress || ClassSubjectStepProgress || UserStepProgress)
    ) {
      await trackProfileCompletion()
    }
    next()
  }

  const handleClose = useCallback(() => {
    setShow(false)
    reset()
  }, [reset])

  const trackProfileCompletion = useCallback(async () => {
    setLoading(true)

    const onSuccess = (res: any) => {
      const descriptionString = res?.result?.[0]?.description || ''
      const matches = descriptionString.match(/\d+/g)
      const lastMatch = matches ? matches.pop() : '0'
      const description = Number(lastMatch)

      const num = isNaN(description) ? 0 : description * ONBOARD_PROGRESS.STEP_1
      const newOnboardProgress = num >= ONBOARD_PROGRESS.STEP_3 ? num + 1 : num
      setOnboardProgress(newOnboardProgress)
      updateFields({onboardProgress: newOnboardProgress})
      setLoading(false)
    }

    const onFailure = () => {
      setLoading(false)
    }

    dispatch(trackProgressBar(onSuccess, onFailure))
  }, [dispatch, updateFields])

  useEffect(() => {
    trackProfileCompletion()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const defaultModalOpen = useCallback(
    (num: number) => {
      setShow(true)
      if (num === 0) goto(0)
      else if (num === ONBOARD_PROGRESS.STEP_1) goto(2)
      else if (num === ONBOARD_PROGRESS.STEP_2) goto(3)
    },
    [goto]
  )

  return (
    <>
      <div className={clsx('d-flex align-items-center', toolbarButtonMarginClass)}>
        <button
          className='btn card-title btn-active-light-primary'
          onClick={() => defaultModalOpen(onboardProgress)}
        >
          <>
            {onboardProgress === ONBOARD_PROGRESS.LAST_STEP ? (
              <OverlayTrigger
                placement='bottom'
                overlay={
                  <Tooltip id={`tooltip-top`}>
                    {intl.formatMessage({id: 'ONBOARDING.CLICK_TO_GET_STARTED'})}
                  </Tooltip>
                }
              >
                <i
                  className='bi bi-check-circle-fill'
                  style={{fontSize: '180%', color: '#2BB06F'}}
                ></i>
              </OverlayTrigger>
            ) : (
              <span>
                {intl.formatMessage({id: 'ONBOARDING.WIDGET_ONBOARD'})}
                <div className='progress'>
                  <div
                    className='progress-bar progress-bar-striped progress-bar-animated'
                    role='progressbar'
                    aria-valuenow={onboardProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    style={{width: `${onboardProgress}%`}}
                  >
                    {onboardProgress}%
                  </div>
                </div>
              </span>
            )}
          </>
        </button>
      </div>
      <Modal
        size={'xl'}
        show={show}
        onHide={handleClose}
        backdrop='static'
        keyboard={false}
        className='mt-10 mb-10'
      >
        <Modal.Header closeButton>
          <Modal.Title>{intl.formatMessage({id: 'ONBOARD_MODAL_TITLE'})}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='pb-8'>
            {loading && (
              <div
                className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                style={{backgroundColor: 'rgba(255, 255, 255, 0.5)', zIndex: 999}}
              >
                <Spinner color='primary' type='grow' />
              </div>
            )}
            <>{step}</>
            <div className='d-flex align-items-center justify-content-between flex-wrap'>
              <div className='d-flex justify-content-center w-100 mt-2'>
                <div className='d-flex justify-content-center'>
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        background:
                          index === currentStepIndex ? getCSSVariableValue('--bs-primary') : 'gray',
                        marginRight: '8px',
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ClientOnboardPage
